import { default as axios } from "axios";
import {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
} from "axios";
import { Observable } from "rxjs";

export default class HttpService {
  protected client: AxiosInstance;
  constructor(params?: AxiosRequestConfig, apiKey?: string, basePath?: string) {
    this.client = axios.create({
      ...params,
      ...this.params,
      baseURL: basePath,
    });
    if (apiKey) {
      this.client.interceptors.request.use((config: AxiosRequestConfig) => {
        this.setAuthHeaders(config, apiKey);
        return config;
      });
    }
    this.client.interceptors.response.use(
      this.onSuccess.bind(this),
      this.onError
    );
  }
  private onSuccess(response: AxiosResponse) {
    return response;
  }

  private onError(error: AxiosError) {
    // log here if needed
    throw error;
  }
  private get params(): AxiosRequestConfig {
    return {
      headers: {
        "Content-Type": "application/json",
      },
    };
  }
  private setAuthHeaders(config: AxiosRequestConfig, apiKey: string) {
    if (!config.headers) {
      config.headers = {};
    }
    config.headers["MindMeKey"] = apiKey;
    return config;
  }

  get = <T>(url: string, queryParams?: any): Observable<T> => {
    //return defer(() => this.client.get<T>(url, { params: queryParams })).pipe(map((result) => result.data));
    return this.httpRequest(url, "GET", queryParams);
  };

  post = <T>(url: string, data: any, extraConfig?: AxiosRequestConfig): Observable<T> => {
    //return defer(() => this.client.post(url, data, progressCallback)).pipe(map((result) => result.data));
    return this.httpRequest(url, "POST", undefined, data, extraConfig);
  };

  put = <T>(url: string, data: any): Observable<T> => {
    //return defer(() => this.client.put(url, data)).pipe(map((result) => result.data));
    return this.httpRequest(url, "PUT", undefined, data);
  };

  delete = <T>(url: string, data?: any): Observable<T> => {
    return this.httpRequest(url, "DELETE", undefined, data);
  };

  patch = <T>(url: string, data: any): Observable<T> => {
    return this.httpRequest(url, "PATCH", undefined, data);
  };

  private httpRequest(
    url: string,
    method: string,
    queryParams?: string,
    data?: any,
    progressCallback?: any
  ): Observable<any> {
    let httpResponse: Promise<AxiosResponse<any, any>>;
    switch (method) {
      case "GET":
        httpResponse = this.client.get<any>(
          queryParams ? `${url}?${queryParams}` : url
        );
        break;
      case "POST":
        httpResponse = this.client.post<any>(url, data, progressCallback);
        break;
      case "DELETE":
        httpResponse = this.client.delete<any>(url, { data });
        break;
      case "PUT":
        httpResponse = this.client.put<any>(url, data);
        break;
      case "PATCH":
        httpResponse = this.client.patch<any>(url, data);
        break;
      default:
        throw new Error("Method not allowed");
    }
    return new Observable<any>((subscriber) => {
      httpResponse
        .then((response: AxiosResponse) => {
          subscriber.next(response.data);
          subscriber.complete();
        })
        .catch((error: AxiosError) => {
          subscriber.error(error);
          subscriber.complete();
        });
    });
  }

  /* GET that hands back the raw response body as a Blob instead of parsed JSON - for endpoints that
     return a file (CSV / XLSX exports). Goes through the same axios client, so the MindMeKey header
     and base URL are applied exactly as for every other call. */
  getBlob = (url: string, queryParams?: string): Observable<Blob> => {
    const httpResponse = this.client.get<Blob>(
      queryParams ? `${url}?${queryParams}` : url,
      { responseType: "blob" }
    );
    return new Observable<Blob>((subscriber) => {
      httpResponse
        .then((response: AxiosResponse<Blob>) => {
          subscriber.next(response.data);
          subscriber.complete();
        })
        .catch((error: AxiosError) => {
          subscriber.error(error);
          subscriber.complete();
        });
    });
  };

  uploadFile = <T>(
    url: string,
    data?: any,
    options?: AxiosRequestConfig
  ): Observable<T> => {
    const httpResponse = this.client.post<any>(url, data, options);
    return new Observable<any>((subscriber) => {
      httpResponse
        .then((response: AxiosResponse) => {
          subscriber.next(response.data);
          subscriber.complete();
        })
        .catch((error: AxiosError) => {
          subscriber.error(error);
          subscriber.complete();
        });
    });
  };
}
